/* eslint-disable @typescript-eslint/naming-convention */
import type { FC, SVGProps } from 'react';

import PublicEnv from 'shared/utils/public-env';
import type { PaymentTypesConfig } from 'src/gql/graphql';
import { CashIcon, CreditCardIcon, CheckIcon } from 'src/payments/icons';

export { CashIcon, CreditCardIcon, CheckIcon } from 'src/payments/icons';

export enum EnrollmentSteps {
  cancel = 'CANCEL',
  connectBank = 'CONNECT_BANK',
  completeEnrollment = 'COMPLETE_ENROLLMENT',
  emailForm = 'EMAIL_FORM',
  forgotPasswordForm = 'FORGOT_PASSWORD_FORM',
  learnMore = 'LEARN_MORE',
  passwordForm = 'PASSWORD_FORM',
  signUpForm = 'SIGN_UP_FORM',
  terms = 'TERMS',
  bankAccountSelector = 'ACCOUNT_SELECTOR',
  bankAccountsLoading = 'BANK_ACCOUNTS_LOADING',
  tosLoading = 'TOS_LOADING',
  unsupportedAccount = 'UNSUPPORTED_ACCOUNT',
  alreadySignedUp = 'ALREADY_SIGNED_UP',
  success = 'SUCCESS',
  startEnrollment = 'START_ENROLLMENT',
  completeAccount = 'COMPLETE_ACCOUNT',
  accountChurn = 'ACCOUNT_CHURN',
}

export enum AeroSyncPreAuthEvent {
  FLOW_STARTED = 'flowStarted',
  FLOW_COMPLETED = 'flowCompleted',
  MFA_PRESENTED = 'mfaPresented',
  MFA_COMPLETED = 'mfaCompleted',
  MFA_ABANDONED = 'mfaAbandoned',
  FLOW_FAILED_FORWARD = 'flowFailedForward',
  FLOW_FAILED = 'flowFailed',
}

export type AeroSyncPreAuthEventMetadata = {
  error?: {
    type: string;
    message: string;
  };
};

export enum StatusCodes {
  denied = 'DENIED',
  challenged = 'CHALLENGED',
  failed = 'FAILED',
  locked = 'LOCKED',
  impeded = 'IMPEDED',
  discontinued = 'DISCONTINUED',
  closed = 'CLOSED',
}

export enum PaymentStatus {
  failed = 'failed',
  succeeded = 'succeeded',
  requiresCapture = 'requires_capture',
}

export enum ConnectionStatus {
  revoked = 'CONNECTION_STATUS_REVOKED',
  aggregatorConnectionError = 'AGGREGATOR_CONNECTION_ERROR',
  pendingExpiration = 'CONNECTION_STATUS_PENDING_EXPIRATION',
}

// Mirrors checkout_flow in Armageddon's telemetry schemas.rb (kept in sync by convention only).
export enum CheckoutFlow {
  ONE_CLICK = 'one_click',
  REDIRECT = 'redirect',
}

export const editBankAccount = 'editBankAccount';
export const relinkBankAccount = 'relinkBankAccount';

export const CHANGE_ACCOUNT = 'Change account';
export const REMOVE_BANK_ACCOUNT = 'Remove';
export const UNLINK_BANK_MODAL_TITLE = 'Remove bank account';

export const PBB_MODAL_CHECKOUT_EXPERIENCE = 'ecomm_modal_cta';

export type SignupButtonLocation =
  | 'cart-rail'
  | 'customer-section'
  | 'ecomm-checkout-payment-section'
  | 'instore-enrollment-pay-by-bank-enrollment-page'
  | 'payment-section';

export const connectingBankSteps = [
  `Syncing to your bank`,
  `Securing account information`,
  `Processing Account Information`,
  `Almost done`,
  `Finishing up`,
];
export const tosSteps = [`Syncing your account`, `Processing account information`, `Almost done`, `Finishing up`];

export enum StepNumberActions {
  decrement = 'decrement',
  increment = 'increment',
}

export type MXRegistrationPayloadProps = {
  mxWidgetUrl: string;
  billingAddressId: string;
};

export type BankAccount = {
  accountAlias: string;
  accountGuid: string;
  availableBalance: number;
  bankName: string;
  lastDigits: string;
};

export type ConnectedBankAccountDetails = {
  ownerName: string;
  bankName: string;
  lastDigits: number | string;
  id: string;
  accountName?: string;
};

export const instoreCartPollingInterval = 5000;

export const AGGREGATORS = {
  PLAID: `plaid`,
  MX: `mx`,
  AEROSYNC: `aerosync`,
};

export enum Aggregator {
  PLAID = 'plaid',
  AEROSYNC = 'aerosync',
}

export const ERROR_TYPES = {
  DUTCHIE_PAY: `dutchiePay`,
  CREDIT: `credit`,
};

export const LOG_NAMESPACES = {
  KIOSK_CHECKOUT: 'kiosk-checkout',
  PBB: 'pbb',
  PBB_CHECKOUT: 'pbb-checkout',
  PBB_KIOSK_CHECKOUT: 'pbb-kiosk',
  PBB_ENROLLMENT: 'pbb-enrollment',
  PBB_INCENTIVES: 'pbb-incentives',
  PBB_INSTORE_CART: 'pbb-instore-cart',
};

export const PLAID_STORAGE_KEY = 'plaidToken';
export enum PlaidLinkCustomizationNames {
  EMBEDDED_SEARCH_INSTORE = 'embedded_search_instore',
  EMBEDDED_SEARCH_ONLINE = 'embedded_search_online',
  EMBEDDED_SEARCH_ONLINE_POPUP = 'embedded_search_online_popup',
  INCENTIVES = 'incentive_applied',
  DEFAULT = 'default',
}

export const IS_E2E = PublicEnv.isE2E === 'true' && PublicEnv.appEnv === 'development';
export const DUTCHIE_PAY_QUERY_PARMAS_KEY = 'dutchiePayQueryParams';

export const PLAID_OAUTH_PATH = 'oauth_state_id';

export const REDIRECT_URL = `${PublicEnv.consumerUrl}/pay/signup`;
export const PLAID_TERMS_URL = `https://dutchie.com/business/legal/digital-money-management-user-terms-plaid`;
export const MONEY_MGMT_TERMS_URL = `https://try.dutchie.com/digital-money-management-user-terms/`;
export const SIGNATURE_TERMS_URL = `https://try.dutchie.com/electronic-signature-agreement/`;
export const PBB_INCENTIVES_TERMS_URL = `https://dutchie.com/business/legal/dutchie-pay-by-bank-sign-up-bonus-terms`;

export const CHECKOUT_PATH = `checkout`;

export const ACCOUNT_SETTINGS_PATH = `account/bank-account`;

export const INSTORE_CART_PATH = `/pay/instore`;
// Online Payment Methods
export const DUTCHIE_PAY = 'dutchiePay';
export const PAY_ONLINE_MERRCO = 'payOnlineMerrco';
export const PAY_ONLINE_MONERIS = 'payOnlineMoneris';
// Instore Payment Methods
export const AEROPAY = 'aeropay';
export const ALT36 = 'alt36';
export const CAN_PAY = 'canPay';
export const CASH = 'cash';
export const CHECK = 'check';
export const CREDIT_CARD_AT_DOOR = 'creditCard';
export const CREDIT_CARD_BY_PHONE = 'creditCardByPhone';
export const DEBIT = 'debitCard';
export const LINX = 'linx';
export const PAYTENDER = 'paytender';

export const PBB_NAME = 'Pay by Bank';

export const PAYMENT_OPTION_ICON_COMPONENTS: Record<string, FC<SVGProps<SVGSVGElement>> | undefined> = {
  cash: CashIcon,
  check: CheckIcon,
  creditCard: CreditCardIcon,
  debitCard: CreditCardIcon,
  debit: CreditCardIcon,
  alt36: CreditCardIcon,
  payOnlineMerrco: CreditCardIcon,
  payOnlineMoneris: CreditCardIcon,
  payOnlineChase: CreditCardIcon,
  creditCardByPhone: CreditCardIcon,
  creditCardAtDoor: CreditCardIcon,
};

export const PAYMENT_OPTION_ICONS = {
  cash: '/icons/payment-methods/cash-icon.svg',
  check: '/icons/payment-methods/check-icon.svg',
  creditCard: '/icons/payment-methods/credit-card-icon.svg',
  debitCard: '/icons/payment-methods/credit-card-icon.svg',
  debit: '/icons/payment-methods/credit-card-icon.svg',
  alt36: '/icons/payment-methods/credit-card-icon.svg',
  linx: '/icons/payment-methods/linx-logo-icon.svg',
  payOnlineHypur: '/icons/payment-methods/hypur-logo-icon.svg',
  payOnlineMerrco: '/icons/payment-methods/credit-card-icon.svg',
  payOnlineMoneris: '/icons/payment-methods/credit-card-icon.svg',
  payOnlineChase: '/icons/payment-methods/credit-card-icon.svg',
  creditCardByPhone: '/icons/payment-methods/credit-card-icon.svg',
  creditCardAtDoor: '/icons/payment-methods/credit-card-icon.svg',
  canPay: '/icons/payment-methods/canpay-logo-icon.svg',
  paytender: '/icons/payment-methods/paytender-logo-icon.svg',
  aeropay: '/icons/payment-methods/aeropay-logo-icon.svg',
};

export const ALL_OFFLINE_PAYMENT_METHODS: (
  | keyof Omit<PaymentTypesConfig, 'creditCardAtDoor' | 'debit'>
  | 'creditCard'
  | 'debitCard'
)[] = [AEROPAY, ALT36, CAN_PAY, CASH, CHECK, CREDIT_CARD_AT_DOOR, CREDIT_CARD_BY_PHONE, DEBIT, LINX, PAYTENDER];

export const CANADA_CREDIT_CART_PAYMENT_METHODS: (keyof PaymentTypesConfig)[] = [PAY_ONLINE_MERRCO, PAY_ONLINE_MONERIS];

export const ONLINE_PAYMENT_METHODS: (keyof PaymentTypesConfig)[] = [
  DUTCHIE_PAY,
  ...CANADA_CREDIT_CART_PAYMENT_METHODS,
];

export const Constants = {
  UNKNOWN_BANK: 'Unknown Bank',
  BANK_DIGITS_MISSING: '----',
  PAYMENT_INFORMATION: 'Payment Information',
  CHANGE: 'Change',
  RELINK: 'Relink',
  ERROR_STATE: 'Bank account needs to be relinked',
  RECONNECT_LABEL: 'Relink your bank account',
};

export const UNKNOWN_BANK = 'Unknown Bank';
export const BANK_DIGITS_MISSING = '----';
export const PAYMENT_INFORMATION = 'Payment Information';
export const CHANGE = 'Change';
export const RELINK = 'Relink';
export const ERROR_STATE = 'Bank account needs to be relinked';
export const RECONNECT_LABEL = 'Relink your bank account';
export const ZERO_FEE = '$0 Fee';
export const FASTER = 'Faster';
export const UNENROLLED_ALERT_MESSAGE = 'Link your bank account, or select a different payment option.';
export const UNENROLLED_ALERT_MESSAGE_WITH_ONLY_PBB = 'Link your bank account to checkout.';
export const RELINK_ALERT_MESSAGE = 'Bank account is unlinked. Relink or change to continue.';

export const PLAID_OFFSET = 4 as const;
export const BASE_MAX_WIDTH = 390 as const;
